// ════════════════════════════════════════════════════════════════════
// Landing — marketing landing page.
// Layout follows slyce.io (hero composite, platform strip, three alternating
// product sections, why cards, partners) in Netrev's own palette, then
// pricing, the demo/contact form and the footer. Nav, footer, base CSS and
// the scroll animations are shared with Our story (SiteChrome.js); the
// illustrations are drawn in code (LandingArt.js). Copy lives in
// lib/i18n-catalog/landing.js.
// ════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";
import { getAdditionalRate, getFirstSiteRate, getFirstSiteAnnual, getAdditionalAnnual } from "../lib/pricing";
import { insertContactSubmission } from "../data/contact";
import { useT } from "../lib/i18n";
import {
  NAVY, ROYAL, BLUE, CAL_BOOKING_URL, LANDING_ANCHOR_KEY, SITE_CSS,
  SiteNav, SiteFooter, BackToTop, fill, tidy, scrollToId, useSiteMotion, useScrollState, useParallax,
} from "./SiteChrome";
import { HeroArt, HeroArtCompact, InsightArt, AiArt, PriceArt, PartnerArt, RingIcon } from "./LandingArt";

const CURRENCIES = [["gbp", "£ GBP"], ["aed", "AED"], ["inr", "₹ INR"]];
const CUR_CODE = { gbp: "GBP", aed: "AED", inr: "INR" };
const PLATFORM_NAMES = ["Deliveroo", "Uber Eats", "Just Eat", "Talabat", "Careem Food", "Noon Food", "Keeta", "Swiggy", "Zomato", "DoorDash"];

// Show prices in the visitor's market by default (they can still switch).
function guessCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Asia/Dubai") return "aed";
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "inr";
  } catch (_) { /* old browser: fall back to GBP */ }
  return "gbp";
}

function formatPrice(cur, amount) {
  if (cur === "aed") return `AED ${amount.toLocaleString("en-AE")}`;
  if (cur === "inr") return `₹${amount.toLocaleString("en-IN")}`;
  return `£${amount.toFixed(2)}`;
}

const Check = ({ d }) => (
  <span className="lp-check" aria-hidden="true" data-reveal="pop" style={d == null ? undefined : { "--d": d }}>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
  </span>
);

// Slyce-style product section: eyebrow, heading, ticked points, CTA + art.
// Each piece rises into view in turn; the art slides in from its side.
function Feature({ id, eyebrow, title, lead, items, children, art, flip, after, rtl }) {
  return (
    <section id={id} className="lp-sec">
      <div className="lp-wrap">
        <div className={"lp-split" + (flip ? " lp-flip" : "")}>
          <div className="lp-copy">
            <div className="lp-eyebrow" data-reveal="">{eyebrow}</div>
            <h2 className="lp-h2" data-reveal="" style={{ "--d": 1 }}>{tidy(title, rtl)}</h2>
            {lead && <p className="lp-lead" data-reveal="" style={{ "--d": 2 }}>{lead}</p>}
            <ul className="lp-list">
              {items.map(([head, body], i) => (
                <li key={i} data-reveal="" style={{ "--d": i + 3 }}><Check d={i + 4} /><div><h3>{head}</h3><p>{body}</p></div></li>
              ))}
            </ul>
            <div data-reveal="" style={{ "--d": items.length + 3 }}>{children}</div>
          </div>
          <div className="lp-art" data-reveal={flip ? "left" : "right"} style={{ "--d": 1 }}>{art}</div>
        </div>
        {after}
      </div>
    </section>
  );
}

const LANDING_CSS = `
  /* PLATFORM STRIP */
  .lp-strip { padding:28px 40px 64px; }
  .lp-strip-label { text-align:center; font-size:14px; font-weight:600; color:var(--muted); margin:0 0 22px; }
  .lp-marquee { overflow:hidden; -webkit-mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent); mask-image:linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent); }
  .lp-marquee-track { display:flex; width:max-content; animation:lpScroll 42s linear infinite; }
  .lp-marquee:hover .lp-marquee-track { animation-play-state:paused; }
  .lp-wordmark { font-family:var(--head); font-size:25px; font-weight:800; color:#7c86a6; letter-spacing:-.6px; white-space:nowrap; margin-right:64px; }
  @keyframes lpScroll { to { transform:translateX(-50%); } }

  /* HERO ART — a compact story-only version on phones */
  .lp-hero-art-compact { display:none; }

  /* WHY */
  .lp-why-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; margin-top:48px; }
  .lp-why-card { background:#fff; border-radius:20px; padding:30px 26px 34px; text-align:center; box-shadow:0 1px 2px rgba(20,34,110,.05), 0 0 0 1px rgba(20,34,110,.05); }
  .lp-why-card:hover { translate:0 -6px; box-shadow:0 22px 40px -24px rgba(20,34,110,.35), 0 0 0 1px rgba(20,34,110,.06); }
  .lp-why-card h3 { font-size:21px; font-weight:800; letter-spacing:-.4px; margin:18px 0 8px; color:var(--ink); }
  .lp-why-card p { margin:0; font-size:15px; line-height:1.55; color:var(--body); }

  /* PARTNERS */
  .lp-tiers { font-size:15px; line-height:1.55; color:var(--ink); font-weight:600; margin:0 0 26px; padding:14px 16px; border-radius:12px; background:var(--tint); border:1px solid var(--line); }
  .lp-formats { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; margin-top:56px; }
  .lp-format { display:flex; gap:14px; align-items:flex-start; padding:20px; border-radius:16px; border:1px solid var(--line); background:#fff; }
  .lp-format:hover { translate:0 -4px; box-shadow:0 18px 34px -24px rgba(20,34,110,.35); }
  .lp-format h3 { font-size:16.5px; font-weight:800; margin:2px 0 4px; letter-spacing:-.3px; color:var(--ink); }
  .lp-format p { margin:0; font-size:14px; color:var(--body); }
  .lp-format-icon { flex-shrink:0; width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg, ${NAVY}, ${ROYAL}); }

  /* PRICING */
  .lp-toggles { display:flex; justify-content:center; gap:12px; flex-wrap:wrap; margin-bottom:30px; }
  .lp-seg { display:inline-flex; background:#fff; border:1px solid var(--line); border-radius:10px; padding:4px; }
  .lp-seg button { padding:8px 16px; border-radius:7px; border:none; background:transparent; color:var(--muted); font-size:13.5px; font-weight:600; cursor:pointer; white-space:nowrap; transition:background .2s, color .2s; }
  .lp-seg button.on { background:var(--navy); color:#fff; }
  .lp-free { color:#047857; font-size:11px; font-weight:700; margin-inline-start:8px; }
  .lp-seg button.on .lp-free { color:#86efac; }
  .lp-price-card { max-width:460px; margin:0 auto; background:#fff; border-radius:20px; padding:34px; text-align:start; box-shadow:0 30px 60px -36px rgba(20,34,110,.45), 0 0 0 1px rgba(20,34,110,.06); }
  .lp-price-label { font-size:13px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.5px; }
  .lp-price { display:flex; align-items:baseline; gap:6px; margin:10px 0 6px; flex-wrap:wrap; }
  .lp-price-num { font-family:var(--head); font-size:46px; font-weight:800; letter-spacing:-1.6px; color:var(--ink); line-height:1; }
  .lp-price span { font-size:15px; color:var(--muted); font-weight:500; }
  .lp-price-save { font-size:13px; color:#047857; font-weight:600; margin-bottom:4px; }
  .lp-price-extra { font-size:13.5px; color:var(--body); font-weight:600; padding-bottom:22px; margin-bottom:22px; border-bottom:1px solid #eef1f6; }
  .lp-price-feats { list-style:none; padding:0; margin:0 0 22px; display:grid; gap:12px; }
  .lp-price-feats li { display:flex; gap:12px; align-items:center; font-size:14.5px; color:#2b3557; font-weight:500; }
  .lp-price-feats .lp-check { width:26px; height:26px; }
  .lp-price-note { font-size:12.5px; line-height:1.55; color:var(--muted); margin:0 0 14px; padding:10px 12px; border-radius:10px; background:var(--tint); }

  /* CONTACT */
  .lp-contact { max-width:760px; }
  .lp-form-card { background:#fff; border-radius:20px; padding:32px; box-shadow:0 30px 60px -36px rgba(20,34,110,.45), 0 0 0 1px rgba(20,34,110,.06); }
  .lp-form-head { margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid #eef1f6; }
  .lp-form-eyebrow { font-size:11px; font-weight:700; color:var(--blue); letter-spacing:1.2px; text-transform:uppercase; margin-bottom:6px; }
  .lp-form-head h3 { font-size:21px; font-weight:800; margin:0; letter-spacing:-.4px; color:var(--ink); }
  .lp-tabs { display:flex; gap:6px; margin-bottom:20px; background:var(--tint); padding:5px; border-radius:11px; }
  .lp-tabs button { flex:1; padding:10px; border-radius:8px; border:none; background:transparent; color:var(--muted); font-size:13.5px; font-weight:600; cursor:pointer; transition:background .2s, color .2s, box-shadow .2s; }
  .lp-tabs button.on { background:#fff; color:var(--ink); box-shadow:0 1px 3px rgba(15,23,42,.1); }
  .lp-form-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .lp-field { margin-bottom:16px; }
  .lp-field label { display:block; font-size:12.5px; font-weight:600; color:var(--ink); margin-bottom:7px; }
  .lp-req { color:#ef4444; }
  .lp-opt { color:var(--muted); font-weight:400; }
  .lp-field input, .lp-field textarea { width:100%; padding:12px 14px; border-radius:10px; border:1.5px solid transparent; font-size:14px; color:var(--ink); background:#f4f5f8; font-family:inherit; outline:none; transition:border-color .15s, background .15s; }
  .lp-field input:focus, .lp-field textarea:focus { border-color:var(--blue); background:#fff; }
  .lp-field textarea { resize:vertical; min-height:110px; }
  .lp-alert { padding:10px 14px; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; font-size:13px; color:#991b1b; margin-bottom:14px; font-weight:500; }
  .lp-sent { padding:14px 16px; background:#dcfce7; border:1px solid #86efac; border-radius:9px; font-size:13.5px; color:#14532d; font-weight:600; display:flex; align-items:center; gap:10px; animation:lpFade .3s ease; }
  .lp-submit { width:100%; padding:16px 20px; border-radius:10px; border:none; background:linear-gradient(90deg, ${NAVY} 0%, ${ROYAL} 100%); color:#fff; font-family:var(--head); font-size:15.5px; font-weight:700; cursor:pointer; box-shadow:0 10px 22px -10px rgba(44,80,221,.7); transition:transform .15s, box-shadow .15s; }
  .lp-submit:hover { transform:translateY(-1px); }
  .lp-submit:disabled { background:#94a3b8; box-shadow:none; cursor:wait; transform:none; }
  .lp-fine { font-size:11.5px; color:var(--muted); text-align:center; margin:12px 0 0; line-height:1.5; }
  .lp-book-note { padding:13px 15px; background:#eef2ff; border-radius:10px; margin-bottom:14px; font-size:13px; color:#26357a; line-height:1.55; }
  .lp-book-frame { border-radius:12px; overflow:hidden; border:1px solid var(--line); background:#fff; }
  .lp-book-foot { display:flex; justify-content:center; gap:6px; flex-wrap:wrap; margin-top:14px; font-size:12.5px; color:var(--muted); }
  .lp-book-foot a { color:var(--blue); font-weight:600; text-decoration:none; }
  .lp-mailline { font-size:14px; color:var(--muted); margin:22px 0 0; }
  .lp-mailline a { color:var(--blue); font-weight:700; text-decoration:none; }

  @media (prefers-reduced-motion: reduce) {
    .lp-marquee { -webkit-mask-image:none; mask-image:none; }
    .lp-marquee-track { animation:none; width:auto; flex-wrap:wrap; justify-content:center; row-gap:16px; }
    .lp-marquee-track > [aria-hidden="true"] { display:none; }
  }
  @media (max-width: 900px) {
    .lp-strip { padding:20px 20px 48px; }
    .lp-wordmark { font-size:21px; margin-right:44px; }
    .lp-why-grid, .lp-formats { grid-template-columns:1fr; }
    .lp-why-grid { gap:16px; margin-top:32px; }
    .lp-why-card { padding:22px 22px 26px; }
    .lp-why-card svg { width:112px; height:112px; }
    .lp-why-card h3 { margin-top:12px; }
    .lp-formats { margin-top:40px; }
  }
  @media (max-width: 600px) {
    .lp-hero-art-full { display:none; }
    .lp-hero-art-compact { display:block; }
  }
  @media (max-width: 520px) {
    .lp-form-card, .lp-price-card { padding:22px 18px; }
    .lp-form-row { grid-template-columns:1fr; gap:0; }
    .lp-seg button { padding:8px 11px; font-size:13px; }
  }
`;

export function Landing({ onStart, onStory, onLegal }) {
  const { lang, t } = useT();
  const rtl = lang === "ar";
  const rootRef = useRef(null);
  const heroArtRef = useRef(null);
  const [billing, setBilling] = useState("monthly");
  const [cur, setCur] = useState(guessCurrency);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", message: "", type: "demo" });
  const [contactStatus, setContactStatus] = useState(""); // "", "submitting", "sent", "error", "error_validation"
  const { scrolled, deep } = useScrollState();

  useSiteMotion(rootRef, `${lang}-${cur}`);
  useParallax(heroArtRef);

  // Arriving from another marketing page (e.g. Our story → Pricing). The key
  // is only cleared once we scroll, so a re-run effect (StrictMode) still sees it.
  useEffect(() => {
    let anchor = null;
    try {
      anchor = window.sessionStorage.getItem(LANDING_ANCHOR_KEY);
    } catch (_) { /* storage blocked: stay at the top */ }
    if (!anchor) return undefined;
    const timer = setTimeout(() => {
      try { window.sessionStorage.removeItem(LANDING_ANCHOR_KEY); } catch (_) { /* ignore */ }
      if (anchor === "book") {
        setContactForm((p) => ({ ...p, type: CAL_BOOKING_URL ? "book" : "demo" }));
        scrollToId("contact");
      } else {
        scrollToId(anchor);
      }
    }, 80);
    return () => clearTimeout(timer);
  }, []);

  async function submitContact() {
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus("error_validation");
      return;
    }
    setContactStatus("submitting");
    try {
      const { error } = await insertContactSubmission({
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company || null,
        message: contactForm.message,
        type: contactForm.type,
      });
      if (error) throw error;
      setContactStatus("sent");
      setTimeout(() => {
        setContactStatus("");
        setContactForm({ name: "", email: "", company: "", message: "", type: "demo" });
      }, 2500);
    } catch (e) {
      console.error("Contact submission error:", e);
      setContactStatus("error");
    }
  }

  function go(id) {
    setMenuOpen(false);
    scrollToId(id);
  }
  function openBooking() {
    setMenuOpen(false);
    setContactForm((p) => ({ ...p, type: CAL_BOOKING_URL ? "book" : "demo" }));
    scrollToId("contact");
  }

  // Per-workspace pricing from lib/pricing (must match Stripe). Annual = pay
  // for the year and get ONE MONTH FREE (11× monthly).
  const annual = billing === "annual";
  const code = CUR_CODE[cur];
  const firstPrice = annual ? getFirstSiteAnnual(code) : getFirstSiteRate(code);
  const extraPrice = annual ? getAdditionalAnnual(code) : getAdditionalRate(code);
  const saving = getFirstSiteRate(code) * 12 - getFirstSiteAnnual(code);
  const perPeriod = annual ? t("landing.pricing.perYear") : t("landing.pricing.perMonth");

  // Translated labels for the floating cards in the illustrations.
  const ded = ["commission", "vat", "card", "cpc", "subscription"].map((k) => t(`landing.art.ded.${k}`));
  const gap = { eyebrow: t("landing.art.gap.eyebrow"), pos: t("landing.art.gap.pos"), bank: t("landing.art.gap.bank"), deducted: t("landing.art.gap.deducted") };
  const heroText = {
    gap, tracked: t("landing.art.tracked"), ded,
    ai: { tag: t("landing.art.ai.tag"), title: t("landing.art.ai.title"), action: t("landing.art.ai.action"), impact: t("landing.art.perMonth") },
  };
  const aiText = {
    bubbleIn: t("landing.art.bubble.in"), bubbleOut: t("landing.art.bubble.out"),
    toggles: [ded[0], ded[1], ded[2], t("landing.art.toggle.cpc"), t("landing.art.toggle.discounts"), ded[4]],
    tags: { cpc: t("landing.art.rec.tagCpc"), discount: t("landing.art.rec.tagDiscount"), platform: t("landing.art.rec.tagPlatform") },
    rec: { cpc: t("landing.art.rec.cpc"), discount: t("landing.art.rec.discount"), grow: t("landing.art.rec.grow"), losing: t("landing.art.rec.losing") },
    perMonth: t("landing.art.perMonth"),
  };
  const priceText = {
    campaign: t("landing.art.price.campaign"), profitPerOrder: t("landing.art.price.profitPerOrder"), loses: t("landing.art.price.loses"),
    discount: t("landing.art.price.discount"), campaignFee: t("landing.art.price.campaignFee"),
  };

  const navLinks = [
    { key: "product", l: t("landing.nav.product"), fn: () => go("product") },
    { key: "partners", l: t("landing.nav.partners"), fn: () => go("partners") },
    { key: "pricing", l: t("landing.nav.pricing"), fn: () => go("pricing") },
    { key: "story", l: t("landing.nav.ourStory"), fn: () => { setMenuOpen(false); if (onStory) onStory(); } },
    { key: "contact", l: t("landing.nav.contact"), fn: () => go("contact") },
  ];

  const footerCols = [
    {
      key: "product", title: t("landing.footer.col.product"),
      links: [
        { key: "insight", l: t("landing.p1.eyebrow"), fn: () => scrollToId("product") },
        { key: "ai", l: t("landing.p2.eyebrow"), fn: () => scrollToId("ai") },
        { key: "price-tool", l: t("landing.p3.eyebrow"), fn: () => scrollToId("price-tool") },
        { key: "pricing", l: t("landing.footer.link.pricing"), fn: () => scrollToId("pricing") },
        { key: "signin", l: t("landing.footer.link.signIn"), fn: () => onStart("login") },
      ],
    },
    {
      key: "company", title: t("landing.footer.col.company"),
      links: [
        { key: "story", l: t("landing.nav.ourStory"), fn: () => onStory && onStory() },
        { key: "partners", l: t("landing.nav.partners"), fn: () => scrollToId("partners") },
        { key: "contact", l: t("landing.footer.link.contact"), fn: () => scrollToId("contact") },
        { key: "demo", l: t("landing.footer.link.bookDemo"), fn: openBooking },
      ],
    },
    {
      key: "legal", title: t("landing.footer.col.legal"),
      links: [
        { key: "privacy", l: t("landing.footer.legal.privacy"), fn: () => onLegal && onLegal("privacy") },
        { key: "terms", l: t("landing.footer.legal.terms"), fn: () => onLegal && onLegal("terms") },
        { key: "cookies", l: t("landing.footer.legal.cookies"), fn: () => onLegal && onLegal("privacy") },
      ],
    },
  ];

  const field = (key, extra = {}) => ({
    id: `lp-contact-${key}`,
    value: contactForm[key],
    onChange: (e) => setContactForm({ ...contactForm, [key]: e.target.value }),
    ...extra,
  });

  const heroMeta = [
    t("landing.hero.metaTrial"),
    t("landing.hero.metaNoCard"),
    fill(t("landing.hero.metaAfterTrial"), { price: formatPrice(cur, getFirstSiteRate(code)) }),
    t("landing.hero.metaCancel"),
  ];

  return (
    <div ref={rootRef} className={"lp" + (rtl ? " lp-rtl" : "")}>
      <style>{SITE_CSS + LANDING_CSS}</style>

      <SiteNav
        t={t} rtl={rtl} links={navLinks} scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen}
        onHome={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        onSignIn={() => onStart("login")} onStartFree={() => onStart("signup")} onBook={openBooking}
      />

      {/* HERO */}
      <section className="lp-hero lp-tint">
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <h1 className="lp-h1" data-reveal="">{tidy(t("landing.hero.h1"), rtl)}</h1>
            <p className="lp-lead" data-reveal="" style={{ "--d": 1 }}>{t("landing.hero.sub")}</p>
            <div className="lp-ctas" data-reveal="" style={{ "--d": 2 }}>
              <button type="button" className="lp-btn lp-btn-primary" onClick={() => onStart("signup")}>{t("landing.hero.ctaTry")}</button>
              <button type="button" className="lp-btn lp-btn-ghost" onClick={openBooking}>{t("landing.hero.ctaDemo")}</button>
            </div>
            <p className="lp-meta" data-reveal="" style={{ "--d": 3 }}>
              {heroMeta.map((seg, i) => (
                // The long trial line may wrap on narrow phones; the short ones stay whole.
                <span key={i}>{i > 0 && " · "}{i === 0 ? seg : <span className="lp-nowrap">{seg}</span>}</span>
              ))}
            </p>
          </div>
          <div className="lp-hero-art" data-reveal="" style={{ "--d": 1 }}>
            <div className="lp-hero-art-full" ref={heroArtRef}>
              <HeroArt label={t("landing.hero.artAlt")} rtl={rtl} text={heroText} />
            </div>
            <div className="lp-hero-art-compact">
              <HeroArtCompact label={t("landing.hero.artAlt")} rtl={rtl} text={heroText} />
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM STRIP */}
      <section className="lp-strip lp-tint" aria-label={t("landing.strip.label")}>
        <div className="lp-wrap" data-reveal="">
          <p className="lp-strip-label">{t("landing.strip.label")}</p>
          <div className="lp-marquee" dir="ltr">
            <div className="lp-marquee-track">
              {[...PLATFORM_NAMES, ...PLATFORM_NAMES].map((n, i) => (
                <span key={i} className="lp-wordmark" aria-hidden={i >= PLATFORM_NAMES.length ? "true" : undefined}>{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHT+ */}
      <Feature
        id="product" rtl={rtl}
        eyebrow={t("landing.p1.eyebrow")}
        title={t("landing.p1.h2")}
        lead={t("landing.p1.lead")}
        items={[1, 2, 3].map((n) => [t(`landing.p1.b${n}.t`), t(`landing.p1.b${n}.d`)])}
        art={<InsightArt label={t("landing.p1.artAlt")} cur={cur} rtl={rtl} text={{ weekDeductions: t("landing.art.weekDeductions"), ded }} />}
      >
        <button type="button" className="lp-btn lp-btn-primary" onClick={() => onStart("signup")}>{t("landing.hero.ctaTry")}</button>
      </Feature>

      {/* AI ANALYTICS */}
      <Feature
        id="ai" flip rtl={rtl}
        eyebrow={t("landing.p2.eyebrow")}
        title={t("landing.p2.h2")}
        lead={t("landing.p2.lead")}
        items={[1, 2, 3].map((n) => [t(`landing.p2.b${n}.t`), t(`landing.p2.b${n}.d`)])}
        art={<AiArt label={t("landing.p2.artAlt")} cur={cur} rtl={rtl} text={aiText} />}
      >
        <button type="button" className="lp-btn lp-btn-primary" onClick={openBooking}>{t("landing.hero.ctaDemo")}</button>
      </Feature>

      {/* PRICE TOOL */}
      <Feature
        id="price-tool" rtl={rtl}
        eyebrow={t("landing.p3.eyebrow")}
        title={t("landing.p3.h2")}
        lead={t("landing.p3.lead")}
        items={[1, 2, 3].map((n) => [t(`landing.p3.b${n}.t`), t(`landing.p3.b${n}.d`)])}
        art={<PriceArt label={t("landing.p3.artAlt")} cur={cur} rtl={rtl} text={priceText} />}
      >
        <button type="button" className="lp-btn lp-btn-primary" onClick={openBooking}>{t("landing.hero.ctaDemo")}</button>
      </Feature>

      {/* WHY */}
      <section id="why" className="lp-sec lp-tint">
        <div className="lp-wrap">
          <h2 className="lp-h2 lp-center" data-reveal="">{tidy(t("landing.why.h2"), rtl)}</h2>
          <div className="lp-why-grid">
            {[["operators", "c1"], ["markets", "c2"], ["app", "c3"]].map(([icon, c], i) => (
              <div key={icon} className="lp-why-card" data-reveal="" style={{ "--d": i + 1 }}>
                <RingIcon kind={icon} />
                <h3>{t(`landing.why.${c}.t`)}</h3>
                <p>{t(`landing.why.${c}.d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS — POS & middleware */}
      <Feature
        id="partners" flip rtl={rtl}
        eyebrow={t("landing.partners.eyebrow")}
        title={t("landing.partners.h2")}
        lead={t("landing.partners.lead")}
        items={[1, 2, 3].map((n) => [t(`landing.partners.b${n}.t`), t(`landing.partners.b${n}.d`)])}
        art={
          <PartnerArt
            label={t("landing.partners.artAlt")}
            rtl={rtl}
            text={{
              pos: t("landing.partners.art.pos"), posSub: t("landing.partners.art.posSub"), netrev: t("landing.partners.art.netrev"),
              terms: t("landing.partners.art.terms"), inLabel: t("landing.partners.art.in"), outLabel: t("landing.partners.art.out"), chips: ded,
            }}
          />
        }
        after={
          <div className="lp-formats">
            {[
              ["f1", "M3 5h18v14H3zM3 9h18M7 13h5"],
              ["f2", "M8 7l-5 5 5 5M16 7l5 5-5 5M13.5 4l-3 16"],
              ["f3", "M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8zM7.5 7.5h.01"],
            ].map(([k, d], i) => (
              <div key={k} className="lp-format" data-reveal="" style={{ "--d": i }}>
                <span className="lp-format-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
                </span>
                <div><h3>{t(`landing.partners.${k}.t`)}</h3><p>{t(`landing.partners.${k}.d`)}</p></div>
              </div>
            ))}
          </div>
        }
      >
        <p className="lp-tiers">{t("landing.partners.tiers")}</p>
        <button type="button" className="lp-btn lp-btn-primary" onClick={openBooking}>{t("landing.partners.cta")}</button>
      </Feature>

      {/* PRICING */}
      <section id="pricing" className="lp-sec lp-tint">
        <div className="lp-wrap lp-center">
          <h2 className="lp-h2" data-reveal="">{tidy(t("landing.pricing.h2"), rtl)}</h2>
          <p className="lp-lead lp-lead-center" data-reveal="" style={{ "--d": 1 }}>{t("landing.pricing.sub")}</p>
          <div className="lp-toggles" data-reveal="" style={{ "--d": 2 }}>
            <div className="lp-seg" role="group" aria-label={t("landing.pricing.billingAria")}>
              {["monthly", "annual"].map((b) => (
                <button type="button" key={b} aria-pressed={billing === b} className={billing === b ? "on" : ""} onClick={() => setBilling(b)}>
                  {b === "monthly" ? t("landing.pricing.toggle.monthly") : <>{t("landing.pricing.toggle.annual")}<span className="lp-free">{t("landing.pricing.toggle.oneMonthFree")}</span></>}
                </button>
              ))}
            </div>
            <div className="lp-seg" role="group" aria-label={t("landing.pricing.currencyAria")}>
              {CURRENCIES.map(([k, l]) => (
                <button type="button" key={k} aria-pressed={cur === k} className={cur === k ? "on" : ""} onClick={() => setCur(k)}><bdi>{l}</bdi></button>
              ))}
            </div>
          </div>
          <div className="lp-price-card" data-reveal="" style={{ "--d": 3 }}>
            <div className="lp-price-label">{t("landing.pricing.perWorkspace")}</div>
            <div className="lp-price">
              <bdi dir="ltr" className="lp-price-num">{formatPrice(cur, firstPrice)}</bdi>
              <span>{perPeriod}</span>
            </div>
            {annual && <div className="lp-price-save">{fill(t("landing.pricing.save"), { price: formatPrice(cur, saving) })}</div>}
            <div className="lp-price-extra">{fill(t("landing.pricing.additional"), { price: formatPrice(cur, extraPrice), period: perPeriod })}</div>
            <ul className="lp-price-feats">
              {["breakdown", "aiInsights", "priceTool", "apps", "cancel"].map((f) => (
                <li key={f}><Check />{t(`landing.pricing.feat.${f}`)}</li>
              ))}
            </ul>
            <p className="lp-price-note">{t("landing.pricing.note")}</p>
            <button type="button" className="lp-btn lp-btn-primary lp-btn-full" onClick={() => onStart("signup")}>{t("landing.pricing.cta")}</button>
          </div>
        </div>
      </section>

      {/* CONTACT — request-a-demo form + Calendly booking (same form as before) */}
      <section id="contact" className="lp-sec">
        <div className="lp-wrap lp-contact">
          <div className="lp-center">
            <h2 className="lp-h2" data-reveal="">{tidy(t("landing.contact.h2"), rtl)}</h2>
            <p className="lp-lead lp-lead-center" data-reveal="" style={{ "--d": 1 }}>{t("landing.contact.sub")}</p>
          </div>
          <div className="lp-form-card" data-reveal="" style={{ "--d": 2 }}>
            <div className="lp-form-head">
              <div className="lp-form-eyebrow">{t("landing.contact.form.eyebrow")}</div>
              <h3>{CAL_BOOKING_URL && contactForm.type === "book" ? t("landing.contact.form.titleBook") : t("landing.contact.form.title")}</h3>
            </div>

            {CAL_BOOKING_URL && (
              <div className="lp-tabs" role="group">
                {[{ k: "demo", l: t("landing.contact.tab.demo") }, { k: "book", l: t("landing.contact.tab.book") }].map((tab) => (
                  <button type="button" key={tab.k} aria-pressed={contactForm.type === tab.k} className={contactForm.type === tab.k ? "on" : ""}
                    onClick={() => setContactForm({ ...contactForm, type: tab.k })}>{tab.l}</button>
                ))}
              </div>
            )}

            {CAL_BOOKING_URL && contactForm.type === "book" ? (
              <div>
                <div className="lp-book-note"><b>{t("landing.contact.book.noticeBold")}</b> {t("landing.contact.book.noticeRest")}</div>
                <div className="lp-book-frame">
                  <iframe
                    src={`${CAL_BOOKING_URL}?embed_domain=netrev.ai&embed_type=Inline&hide_event_type_details=0&hide_gdpr_banner=1&primary_color=3b5bdb&text_color=111827&background_color=ffffff`}
                    title={t("landing.contact.book.iframeTitle")}
                    width="100%"
                    height="700"
                    frameBorder="0"
                    style={{ display: "block", border: "none", minHeight: 600 }}
                    loading="lazy"
                  />
                </div>
                <div className="lp-book-foot">
                  <span>{t("landing.contact.book.noSlot")}</span>
                  <a href={CAL_BOOKING_URL} target="_blank" rel="noopener noreferrer">{t("landing.contact.book.openFull")} {rtl ? "↖" : "↗"}</a>
                </div>
              </div>
            ) : (
              <>
                <div className="lp-form-row">
                  <div className="lp-field">
                    <label htmlFor="lp-contact-name">{t("landing.contact.form.nameLabel")}<span className="lp-req">*</span></label>
                    <input {...field("name")} placeholder={t("landing.contact.form.namePlaceholder")} />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-contact-email">{t("landing.contact.form.emailLabel")}<span className="lp-req">*</span></label>
                    <input {...field("email", { type: "email" })} placeholder={t("landing.contact.form.emailPlaceholder")} />
                  </div>
                </div>
                <div className="lp-field">
                  <label htmlFor="lp-contact-company">{t("landing.contact.form.companyLabel")}<span className="lp-opt">{t("landing.contact.form.companyOptional")}</span></label>
                  <input {...field("company")} placeholder={t("landing.contact.form.companyPlaceholder")} />
                </div>
                <div className="lp-field">
                  <label htmlFor="lp-contact-message">{t("landing.contact.form.messageLabel")}<span className="lp-req">*</span></label>
                  <textarea {...field("message")} rows={4} placeholder={t("landing.contact.form.messagePlaceholder")} />
                </div>
                {contactStatus === "error_validation" && <div className="lp-alert">{t("landing.contact.form.errValidation")}</div>}
                {contactStatus === "error" && <div className="lp-alert">{t("landing.contact.form.errSend")}</div>}
                {contactStatus === "sent" ? (
                  <div className="lp-sent">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#14532d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    {t("landing.contact.form.sent")}
                  </div>
                ) : (
                  <button type="button" className="lp-submit" onClick={submitContact} disabled={contactStatus === "submitting"}>
                    {contactStatus === "submitting" ? t("landing.contact.form.sending") : t("landing.contact.form.submit")}
                  </button>
                )}
                <p className="lp-fine">{t("landing.contact.form.disclaimer")}</p>
              </>
            )}
          </div>
          <p className="lp-center lp-mailline">{t("landing.contact.preferEmail")} <a href="mailto:hello@netrev.ai">hello@netrev.ai</a></p>
        </div>
      </section>

      <SiteFooter t={t} cols={footerCols} />
      <BackToTop show={deep} label={t("landing.backToTop")} />
    </div>
  );
}
